import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import { X, ThumbsUp, Loader2, MessageSquare, CheckCircle, AlertCircle, Sparkles, Target, Clock } from "lucide-react";

const SPEED_OPTIONS = [
  { value: "slowest", label: "Slowest", desc: "1 vote / 6 min", ms: 360000 },
  { value: "slow", label: "Slow", desc: "1 vote / 5 min", ms: 300000 },
  { value: "normal", label: "Normal", desc: "2 votes / min", ms: 30000 },
  { value: "fast", label: "Fast", desc: "3 votes / min", ms: 20000 },
];

export default function UpvoteModal({ entries = [], onClose, onDone }) {
  const { api } = useAuth();
  const [tab, setTab] = useState("upvote");
  const [quantity, setQuantity] = useState(5);
  const [speed, setSpeed] = useState("slowest");
  const [commentText, setCommentText] = useState("");
  const [commentEntryId, setCommentEntryId] = useState(entries[0]?.id || "");
  const [commentMode, setCommentMode] = useState("reply_to_comment");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [rank1Data, setRank1Data] = useState(null);
  const [loadingRank, setLoadingRank] = useState(false);
  const [aiReplies, setAiReplies] = useState([]);
  const [generatingAi, setGeneratingAi] = useState(false);

  // Fetch rank-1 estimate on mount
  useEffect(() => {
    if (entries.length === 0) return;
    setLoadingRank(true);
    api.post("/buyupvotes/rank1-estimate", { entry_ids: entries.map(e => e.id), quantity: 5, speed: "slowest" })
      .then(r => setRank1Data(r.data))
      .catch(() => {})
      .finally(() => setLoadingRank(false));
  }, [api, entries]);

  const totalUpvotesNeeded = rank1Data?.reduce((s, r) => s + (r.upvotes_needed || 0), 0) || 0;
  const totalCommentsNeeded = rank1Data?.reduce((s, r) => s + (r.comments_needed || 0), 0) || 0;
  const selectedSpeed = SPEED_OPTIONS.find(s => s.value === speed);
  const estimatedTime = selectedSpeed ? Math.ceil(quantity * (selectedSpeed.ms / 60000)) : 0;

  const handleUpvote = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await api.post("/buyupvotes/upvote", { entry_ids: entries.map(e => e.id), quantity, speed });
      const d = res.data;
      setResult({
        status: d.failed === 0 ? "success" : d.submitted > 0 ? "partial" : "error",
        message: `${d.submitted} order(s) submitted, ${d.failed} failed`,
        details: d.results,
      });
      if (d.submitted > 0 && onDone) onDone();
    } catch (err) {
      setResult({ status: "error", message: err.response?.data?.detail || "Failed to submit" });
    } finally {
      setSending(false);
    }
  };

  const handleComment = async () => {
    if (!commentText.trim() || !commentEntryId) return;
    setSending(true);
    setResult(null);
    try {
      const res = await api.post("/buyupvotes/comment", { entry_id: commentEntryId, content: commentText });
      setResult({ status: "success", message: `Comment order submitted (Order #${res.data.order_number})` });
      setCommentText("");
      if (onDone) onDone();
    } catch (err) {
      setResult({ status: "error", message: err.response?.data?.detail || "Failed to submit comment" });
    } finally {
      setSending(false);
    }
  };

  const handleGenerateAi = async () => {
    const eid = commentEntryId || entries[0]?.id;
    if (!eid) return;
    setGeneratingAi(true);
    setAiReplies([]);
    try {
      const entry = entries.find(e => e.id === eid);
      const mode = entry?.entry_type === "Post" ? "comment_on_post" : commentMode;
      const res = await api.post("/buyupvotes/generate-comment", { entry_id: eid, mode });
      setAiReplies(res.data.replies || []);
    } catch (err) {
      setResult({ status: "error", message: err.response?.data?.detail || "AI generation failed" });
    } finally {
      setGeneratingAi(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose} data-testid="upvote-modal-overlay">
      <div onClick={e => e.stopPropagation()} style={{
        background: "#111113", border: "1px solid #27272a", borderRadius: 12,
        width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto",
        boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
      }} data-testid="upvote-modal">
        {/* Header tabs */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #1e1e1e" }}>
          <div style={{ display: "flex", gap: 0 }}>
            {[
              { key: "upvote", label: "Upvote", icon: ThumbsUp, color: "#f97316" },
              { key: "comment", label: "Comment", icon: MessageSquare, color: "#3b82f6" },
            ].map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); setResult(null); }}
                data-testid={`tab-${t.key}`}
                style={{
                  padding: "6px 16px", fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
                  background: tab === t.key ? `${t.color}18` : "transparent",
                  color: tab === t.key ? t.color : "#6b7280",
                  border: `1px solid ${tab === t.key ? `${t.color}44` : "transparent"}`,
                  borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                }}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}><X size={18} /></button>
        </div>

        <div style={{ padding: "16px 20px" }}>
          {/* Rank-1 Estimate Banner */}
          {rank1Data && rank1Data.length > 0 && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 14,
              background: "#0f1a2a", border: "1px solid #1e3a5f33",
              display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap",
            }} data-testid="rank1-estimate">
              <Target size={14} color="#60a5fa" />
              <div style={{ fontSize: 12, color: "#9ca3af" }}>
                <strong style={{ color: "#60a5fa" }}>To reach #1:</strong>
                {" "}<span style={{ color: "#f97316" }}>{totalUpvotesNeeded}</span> upvotes
                {totalCommentsNeeded > 0 && <> + <span style={{ color: "#3b82f6" }}>{totalCommentsNeeded}</span> comments</>} needed
              </div>
              {rank1Data.length === 1 && rank1Data[0].current_rank === 1 && (
                <span style={{ fontSize: 11, color: "#34d399" }}>Already #1</span>
              )}
            </div>
          )}

          {/* Entry info */}
          <div style={{ fontSize: 12, color: "#71717a", marginBottom: 12 }}>
            {entries.length} entr{entries.length === 1 ? "y" : "ies"} selected
            <span style={{ color: "#52525b", marginLeft: 8 }}>via buyupvotes.io</span>
          </div>

          {/* ===== UPVOTE TAB ===== */}
          {tab === "upvote" && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 6 }}>Quantity per entry (5-100):</label>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {[5, 10, 15, 25, 50].map(n => (
                    <button key={n} onClick={() => setQuantity(n)} data-testid={`qty-${n}`}
                      style={{
                        padding: "5px 12px", fontSize: 13, borderRadius: 6, cursor: "pointer",
                        border: `1px solid ${quantity === n ? "#f97316" : "#27272a"}`,
                        background: quantity === n ? "#3d1f07" : "#111113",
                        color: quantity === n ? "#f97316" : "#6b7280", fontWeight: quantity === n ? 700 : 400,
                      }}>{n}</button>
                  ))}
                  <input type="number" min={5} max={100} value={quantity}
                    onChange={e => setQuantity(Math.min(100, Math.max(5, parseInt(e.target.value) || 5)))}
                    style={{ width: 52, padding: "5px 6px", fontSize: 13, borderRadius: 6, border: "1px solid #27272a", background: "#0a0a0a", color: "#e5e5e5", textAlign: "center" }}
                    data-testid="qty-input" />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 6 }}>Delivery speed:</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {SPEED_OPTIONS.map(s => (
                    <button key={s.value} onClick={() => setSpeed(s.value)} data-testid={`speed-${s.value}`}
                      style={{
                        padding: "5px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer",
                        border: `1px solid ${speed === s.value ? "#f97316" : "#27272a"}`,
                        background: speed === s.value ? "#3d1f07" : "#111113",
                        color: speed === s.value ? "#f97316" : "#6b7280", fontWeight: speed === s.value ? 600 : 400,
                      }}>
                      {s.label} <span style={{ fontSize: 10, opacity: 0.7 }}>({s.desc})</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* Time estimate */}
              <div style={{ fontSize: 11, color: "#52525b", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                <Clock size={11} />
                {quantity * entries.length} total upvotes across {entries.length} entries
                {estimatedTime > 0 && <> — ~{estimatedTime > 60 ? `${Math.floor(estimatedTime / 60)}h ${estimatedTime % 60}m` : `${estimatedTime} min`} per entry</>}
              </div>
            </>
          )}

          {/* ===== COMMENT TAB ===== */}
          {tab === "comment" && (
            <>
              {entries.length > 1 && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 4 }}>Comment on:</label>
                  <select value={commentEntryId} onChange={e => { setCommentEntryId(e.target.value); setAiReplies([]); }}
                    data-testid="comment-entry-select"
                    style={{ width: "100%", padding: "7px 10px", fontSize: 12, borderRadius: 6, border: "1px solid #27272a", background: "#0a0a0a", color: "#e5e5e5" }}>
                    {entries.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.entry_type === "Post" ? "Post" : "Comment"} in r/{e.subreddit} — {(e.url || "").slice(-30)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Comment mode */}
              {entries.find(e => e.id === commentEntryId)?.entry_type !== "Post" && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 4 }}>Comment type:</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[
                      { value: "reply_to_comment", label: "Reply to comment", desc: "Boost the comment" },
                      { value: "comment_on_post", label: "Comment on post", desc: "New top-level comment" },
                    ].map(m => (
                      <button key={m.value} onClick={() => { setCommentMode(m.value); setAiReplies([]); }}
                        style={{
                          padding: "5px 12px", fontSize: 11, borderRadius: 6, cursor: "pointer",
                          border: `1px solid ${commentMode === m.value ? "#3b82f6" : "#27272a"}`,
                          background: commentMode === m.value ? "#1e3a5f" : "#111113",
                          color: commentMode === m.value ? "#60a5fa" : "#6b7280",
                        }}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Generate button */}
              <div style={{ marginBottom: 10 }}>
                <button onClick={handleGenerateAi} disabled={generatingAi}
                  data-testid="ai-generate-btn"
                  style={{
                    padding: "6px 14px", fontSize: 12, borderRadius: 6, cursor: "pointer",
                    border: "1px solid #a855f744", background: "#1a0f2e",
                    color: "#a855f7", display: "flex", alignItems: "center", gap: 6,
                  }}>
                  {generatingAi ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {generatingAi ? "Generating..." : "Generate AI Comment"}
                </button>
              </div>

              {/* AI Replies */}
              {aiReplies.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, color: "#71717a", marginBottom: 4, display: "block" }}>AI suggestions (click to use):</label>
                  {aiReplies.map((r, i) => (
                    <div key={i} onClick={() => setCommentText(r)}
                      data-testid={`ai-reply-${i}`}
                      style={{
                        padding: "8px 10px", marginBottom: 4, borderRadius: 6, cursor: "pointer",
                        background: commentText === r ? "#1e3a5f" : "#0a0a0a",
                        border: `1px solid ${commentText === r ? "#3b82f644" : "#1e1e1e"}`,
                        fontSize: 12, color: "#d4d4d4", lineHeight: 1.5,
                        transition: "background 0.15s",
                      }}>
                      <span style={{ color: "#52525b", fontSize: 10, marginRight: 6 }}>#{i + 1}</span>
                      {r}
                    </div>
                  ))}
                </div>
              )}

              {/* Comment text */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 4 }}>Comment text:</label>
                <textarea value={commentText} onChange={e => setCommentText(e.target.value)}
                  placeholder="Your comment... Use [link text](url) for links, [newline] for line breaks"
                  data-testid="comment-text"
                  style={{
                    width: "100%", minHeight: 80, padding: "10px", fontSize: 13, borderRadius: 8,
                    border: "1px solid #27272a", background: "#0a0a0a", color: "#e5e5e5",
                    resize: "vertical", fontFamily: "inherit", lineHeight: 1.5,
                  }} />
                <div style={{ fontSize: 11, color: "#52525b", marginTop: 3 }}>{commentText.length} chars</div>
              </div>

              {/* Speed note */}
              <div style={{ fontSize: 11, color: "#52525b", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <Clock size={11} />
                Comment delivery: ~30 min (slowest available)
              </div>
            </>
          )}

          {/* Result */}
          {result && (
            <div style={{
              padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 12, display: "flex", alignItems: "flex-start", gap: 8,
              background: result.status === "success" ? "#0f291a" : result.status === "partial" ? "#29200f" : "#2a0f0f",
              border: `1px solid ${result.status === "success" ? "#10b98133" : result.status === "partial" ? "#f59e0b33" : "#ef444433"}`,
              color: result.status === "success" ? "#10b981" : result.status === "partial" ? "#f59e0b" : "#ef4444",
            }} data-testid="order-result">
              {result.status === "success" ? <CheckCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />}
              <div>
                {result.message}
                {result.details?.filter(d => d.status === "error").map((d, i) => (
                  <div key={i} style={{ fontSize: 11, marginTop: 2, color: "#ef4444" }}>{d.entry_id?.slice(0, 8)}: {d.message}</div>
                ))}
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={tab === "upvote" ? handleUpvote : handleComment}
            disabled={sending || (tab === "comment" && !commentText.trim())}
            data-testid="order-submit"
            style={{
              width: "100%", padding: "10px", borderRadius: 8, cursor: "pointer",
              background: sending ? "#27272a" : tab === "upvote" ? "#f97316" : "#3b82f6",
              color: "#fff", fontWeight: 700, fontSize: 14, border: "none",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              opacity: (tab === "comment" && !commentText.trim()) ? 0.5 : 1,
            }}>
            {sending ? <Loader2 size={16} className="animate-spin" /> : tab === "upvote" ? <ThumbsUp size={16} /> : <MessageSquare size={16} />}
            {sending ? "Submitting..." : tab === "upvote" ? `Send ${quantity * entries.length} Upvotes` : "Post Comment"}
          </button>
        </div>
      </div>
    </div>
  );
}
